import React, { useState, useEffect } from 'react'
import { Button, Row, Col, Switch, Drawer, message, Card, Space, Typography, Tooltip, Flex } from 'antd'
import {
  CloseOutlined,
  ShoppingCartOutlined,
  ShoppingOutlined,
  PlusOutlined,
  UserAddOutlined,
  BarChartOutlined,
  SettingOutlined,
  QrcodeOutlined,
  BellOutlined,
  TagsOutlined,
  FileImageOutlined,
  TeamOutlined,
  GiftOutlined,
  ThunderboltOutlined,
  EyeOutlined,
  ShareAltOutlined,
} from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { getAppState } from '../../../redux/app/actions'
import StoreQRCode from '../../../components/StoreQrCode'
import { useNavigate } from 'react-router-dom'
import { ROUTE_PATHS } from '../../../routes/constants'
import api from '../../../utils/api'
import { getStoreUrl } from '../../../utils/store'

const { Text, Title } = Typography

interface Props {
  isOpen: boolean
  setOpen: (open: boolean) => void
  url?: string
}

const QuickActionDrawer: React.FC<Props> = ({ isOpen, setOpen, url }) => {
  const [isStoreOnline, setIsStoreOnline] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const appState = useSelector(getAppState)
  const { selectedStore } = appState
  const navigate = useNavigate()

  // Fetch store status when drawer opens
  useEffect(() => {
    if (isOpen) {
      fetchStoreStatus()
    }
  }, [isOpen])

  const fetchStoreStatus = async () => {
    try {
      setIsLoading(true)
      const response = await api.get('/store/status')
      setIsStoreOnline(response.store_online)
    } catch (error) {
      message.error('Failed to fetch store status')
      console.error('Error fetching store status:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleStoreStatusChange = async (checked: boolean) => {
    try {
      setIsLoading(true)

      const response = await api.patch('/store/toggle-status', {
        store_online: checked,
      })

      setIsStoreOnline(response.store_online)
      message.success(response.message)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update store status')
      console.error('Error updating store status:', error)
      // Revert the switch if API call fails
      setIsStoreOnline(!checked)
    } finally {
      setIsLoading(false)
    }
  }

  const storeUrl = React.useMemo(() => {
    if (url) return url
    return getStoreUrl(selectedStore?.store_domain)
  }, [url, selectedStore?.store_domain])

  const handleClose = () => setOpen(false)

  const handleNavigateAndClose = (path: string) => {
    navigate(path)
    handleClose()
  }

  const visitStore = () => {
    if (storeUrl) {
      window.open(storeUrl, '_blank')
    } else {
      message.error('Store URL not available')
    }
  }

  const shareStore = () => {
    if (storeUrl && navigator.share) {
      navigator
        .share({
          title: `${selectedStore?.store_domain} - Online Store`,
          text: `Check out my store: ${selectedStore?.store_domain}`,
          url: storeUrl,
        })
        .catch(() => {
          // Fallback to copying to clipboard
          navigator.clipboard.writeText(storeUrl)
          message.success('Store URL copied to clipboard!')
        })
    } else if (storeUrl) {
      navigator.clipboard.writeText(storeUrl)
      message.success('Store URL copied to clipboard!')
    }
  }

  // Quick action items configuration
  const quickActions = [
    {
      title: 'Add Product',
      description: 'Create a new product',
      icon: <PlusOutlined />,
      path: ROUTE_PATHS.PRODUCT_CREATE,
      color: '#52c41a',
    },
    {
      title: 'New Order',
      description: 'Create manual order',
      icon: <ShoppingCartOutlined />,
      path: ROUTE_PATHS.ORDER_CREATE,
      color: '#1890ff',
    },
    {
      title: 'Add Customer',
      description: 'Register new customer',
      icon: <UserAddOutlined />,
      path: ROUTE_PATHS.CUSTOMER_CREATE,
      color: '#722ed1',
    },
    {
      title: 'Add Category',
      description: 'Create product category',
      icon: <TagsOutlined />,
      path: ROUTE_PATHS.CATEGORY_CREATE,
      color: '#fa541c',
    },
    {
      title: 'Upload Media',
      description: 'Add images & files',
      icon: <FileImageOutlined />,
      path: ROUTE_PATHS.ADD_MEDIA,
      color: '#13c2c2',
    },
    {
      title: 'Team Member',
      description: 'Invite team member',
      icon: <TeamOutlined />,
      path: ROUTE_PATHS.TEAM_CREATE,
      color: '#eb2f96',
    },
  ]

  // Store insights (mock data - replace with real data)
  const storeInsights = [
    { label: "Today's Orders", value: '12', trend: '+3' },
    { label: 'Online Visitors', value: '45', trend: '+8' },
    { label: 'Cart Conversions', value: '26%', trend: '+2%' },
    { label: 'Revenue Today', value: '₹2,450', trend: '+₹320' },
  ]

  return (
    <Drawer
      title={
        <Space size="middle">
          <ThunderboltOutlined style={{ color: '#1890ff' }} />
          <Text strong>Quick Actions & Store Control</Text>
        </Space>
      }
      open={isOpen}
      closeIcon={<CloseOutlined />}
      onClose={handleClose}
      placement="right"
      width={420}
      styles={{
        body: { padding: '16px' },
        header: { borderBottom: '1px solid #f0f0f0' },
      }}
      extra={
        <Space>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Store Status
          </Text>
          <Switch
            checkedChildren="Online"
            unCheckedChildren="Offline"
            checked={isStoreOnline}
            loading={isLoading}
            onChange={handleStoreStatusChange}
          />
        </Space>
      }
    >
      {/* QR Code Section */}
      <Card
        size="small"
        title={
          <Space>
            <QrcodeOutlined />
            <Text>Store QR Code</Text>
          </Space>
        }
        style={{ marginBottom: 16, borderRadius: 8 }}
        bodyStyle={{ padding: '16px', textAlign: 'center' }}
      >
        <StoreQRCode url={storeUrl} />
        <Text type="secondary" style={{ fontSize: '12px', marginTop: 8, display: 'block' }}>
          Share this QR code with customers to visit your store
        </Text>
      </Card>

      {/* Store Overview Card */}
      <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }} bodyStyle={{ padding: '12px 16px' }}>
        <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
          <Space>
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: isStoreOnline ? '#52c41a' : '#ff4d4f',
              }}
            />
            <Text strong>{selectedStore?.store_domain}</Text>
          </Space>
          <Space size="small">
            <Tooltip title="View Store">
              <Button type="text" size="small" icon={<EyeOutlined />} onClick={visitStore} />
            </Tooltip>
            <Tooltip title="Share Store">
              <Button type="text" size="small" icon={<ShareAltOutlined />} onClick={shareStore} />
            </Tooltip>
          </Space>
        </Flex>

        {/* Quick Store Insights */}
        <Row gutter={[8, 8]}>
          {storeInsights.map((insight, index) => (
            <Col span={12} key={index}>
              <div style={{ textAlign: 'center', padding: '8px 4px' }}>
                <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
                  {insight.label}
                </Text>
                <Text strong style={{ fontSize: '14px', display: 'block' }}>
                  {insight.value}
                </Text>
                <Text
                  style={{
                    fontSize: '10px',
                    color: insight.trend.startsWith('+') ? '#52c41a' : '#ff4d4f',
                  }}
                >
                  {insight.trend}
                </Text>
              </div>
            </Col>
          ))}
        </Row>
      </Card>

      {/* Quick Actions Grid */}
      <div style={{ marginBottom: 16 }}>
        <Title level={5} style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600 }}>
          <GiftOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          Quick Actions
        </Title>
        <Row gutter={[8, 8]}>
          {quickActions.map((action, index) => (
            <Col span={12} key={index}>
              <Card
                size="small"
                hoverable
                style={{
                  borderRadius: 8,
                  borderColor: '#f0f0f0',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                }}
                bodyStyle={{ padding: '12px' }}
                onClick={() => handleNavigateAndClose(action.path)}
              >
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: `${action.color}15`,
                        color: action.color,
                        fontSize: '16px',
                        marginBottom: 4,
                      }}
                    >
                      {action.icon}
                    </div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <Text strong style={{ fontSize: '12px', display: 'block' }}>
                      {action.title}
                    </Text>
                    <Text type="secondary" style={{ fontSize: '10px' }}>
                      {action.description}
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      {/* Quick Navigation */}
      <div>
        <Title level={5} style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600 }}>
          <BarChartOutlined style={{ marginRight: 8, color: '#1890ff' }} />
          Quick Navigation
        </Title>
        <Row gutter={[8, 8]}>
          <Col span={8}>
            <Button
              block
              size="small"
              icon={<ShoppingOutlined />}
              onClick={() => handleNavigateAndClose(ROUTE_PATHS.PRODUCTS)}
            >
              Products
            </Button>
          </Col>
          <Col span={8}>
            <Button
              block
              size="small"
              icon={<ShoppingCartOutlined />}
              onClick={() => handleNavigateAndClose(ROUTE_PATHS.ORDERS)}
            >
              Orders
            </Button>
          </Col>
          <Col span={8}>
            <Button
              block
              size="small"
              icon={<BarChartOutlined />}
              onClick={() => handleNavigateAndClose(ROUTE_PATHS.REPORTS_SALES)}
            >
              Reports
            </Button>
          </Col>
          <Col span={12}>
            <Button
              block
              size="small"
              icon={<SettingOutlined />}
              onClick={() => handleNavigateAndClose(ROUTE_PATHS.SETTINGS)}
            >
              Store Settings
            </Button>
          </Col>
          <Col span={12}>
            <Button
              block
              size="small"
              icon={<BellOutlined />}
              onClick={() => handleNavigateAndClose(ROUTE_PATHS.NOTIFICATIONS)}
            >
              Notifications
            </Button>
          </Col>
        </Row>
      </div>
    </Drawer>
  )
}

export default QuickActionDrawer
